import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { BehaviorSubject } from 'rxjs';

// =============================================================================
// PersonalizationService — per-user colour customization.
//
// Each customizable colour is exposed as a CSS custom property (e.g.
// --u-input-bg) AND gated behind a body class (e.g. `u-input-bg`). The service
// only sets the property + adds the class when the user actually customizes
// that colour, so the styling hooks in _personalization.scss are complete
// no-ops until opted into — a user's preference can never break the default
// light/dark themes for elements they didn't touch.
//
// Persisted to localStorage['fn_appearance'] and re-applied on load. SSR-safe.
// =============================================================================

export interface AppearanceToken {
  key: string;          // stable id, e.g. 'input-bg'
  cssVar: string;       // '--u-input-bg'
  bodyClass: string;    // 'u-input-bg'
  label: string;
  description: string;
  defaultLight: string; // swatch starting value in light theme
  defaultDark: string;  // swatch starting value in dark theme
}

export const APPEARANCE_TOKENS: AppearanceToken[] = [
  {
    key: 'input-bg', cssVar: '--u-input-bg', bodyClass: 'u-input-bg',
    label: 'Textbox background', description: 'Fill colour of text inputs, text areas and dropdowns.',
    defaultLight: '#ffffff', defaultDark: '#0c2044',
  },
  {
    key: 'input-text', cssVar: '--u-input-text', bodyClass: 'u-input-text',
    label: 'Textbox text', description: 'Colour of the text you type into fields.',
    defaultLight: '#1a2b3c', defaultDark: '#eaf1fb',
  },
  {
    key: 'accent', cssVar: '--u-accent', bodyClass: 'u-accent',
    label: 'Accent', description: 'Links and primary action colour.',
    defaultLight: '#055cc2', defaultDark: '#5aa2ff',
  },
  {
    key: 'page-bg', cssVar: '--u-page-bg', bodyClass: 'u-page-bg',
    label: 'Page background', description: 'The main working area behind your content.',
    defaultLight: '#f4f8fb', defaultDark: '#07142b',
  },
  {
    key: 'surface', cssVar: '--u-surface', bodyClass: 'u-surface',
    label: 'Card / panel background', description: 'Background of cards, notes and dialogs.',
    defaultLight: '#ffffff', defaultDark: '#0f2547',
  },
  {
    key: 'text', cssVar: '--u-text', bodyClass: 'u-text',
    label: 'Primary text', description: 'Default body and heading text colour.',
    defaultLight: '#1a2b3c', defaultDark: '#eaf1fb',
  },
  {
    key: 'note-bg', cssVar: '--u-note-bg', bodyClass: 'u-note-bg',
    label: 'Note editor background', description: 'Fill colour of the note writing area.',
    defaultLight: '#ffffff', defaultDark: '#0c2044',
  },
  {
    key: 'note-text', cssVar: '--u-note-text', bodyClass: 'u-note-text',
    label: 'Note editor text', description: 'Colour of the text you write in notes.',
    defaultLight: '#1a2b3c', defaultDark: '#eaf1fb',
  },
];

const STORAGE_KEY = 'fn_appearance';

@Injectable({ providedIn: 'root' })
export class PersonalizationService {
  private overrides: Record<string, string> = {};
  private readonly isBrowser: boolean;

  private readonly _changed = new BehaviorSubject<void>(undefined);
  /** Emits whenever an override changes (for live preview / reactivity). */
  readonly changed$ = this._changed.asObservable();

  readonly tokens = APPEARANCE_TOKENS;

  constructor(@Inject(PLATFORM_ID) platformId: Object) {
    this.isBrowser = isPlatformBrowser(platformId);
    if (this.isBrowser) {
      try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) { this.overrides = JSON.parse(raw) || {}; }
      } catch { this.overrides = {}; }
      this.applyAll();
    }
  }

  /** Current override for a token, or undefined if not customized. */
  get(key: string): string | undefined { return this.overrides[key]; }

  isCustomized(key: string): boolean {
    return Object.prototype.hasOwnProperty.call(this.overrides, key) && !!this.overrides[key];
  }

  get anyCustomized(): boolean { return Object.keys(this.overrides).length > 0; }

  // ---------------------------------------------------------------------------
  // TinyMCE lives inside an <iframe> with its own document, so the CSS hooks in
  // _personalization.scss can't reach it. Instead, the note editors append this
  // string to their `content_style` (injected INTO the iframe doc) and re-init
  // on changed$. Returns '' when the user hasn't customized either note colour,
  // so the editor's baked-in oxide/oxide-dark skin still fully governs. A single
  // `body {}` rule: plain text inherits the colour, while content with its own
  // explicit colour (block headers, h1-h3) keeps it (a child's own value beats
  // inherited !important). Values are validated 6-hex on setColor().
  // ---------------------------------------------------------------------------
  tinyNoteContentStyle(): string {
    const bg = this.get('note-bg');
    const text = this.get('note-text');
    if (!bg && !text) { return ''; }
    let decl = '';
    if (bg) { decl += `background-color:${bg} !important;`; }
    if (text) { decl += `color:${text} !important;`; }
    return `body{${decl}}`;
  }

  setColor(key: string, value: string): void {
    if (!this.token(key) || !/^#([0-9a-fA-F]{6})$/.test(value)) { return; }
    this.overrides[key] = value.toLowerCase();
    this.apply(key);
    this.persist();
  }

  resetColor(key: string): void {
    if (!(key in this.overrides)) { return; }
    delete this.overrides[key];
    this.unapply(key);
    this.persist();
  }

  resetAll(): void {
    this.tokens.forEach((t) => this.unapply(t.key));
    this.overrides = {};
    this.persist();
  }

  // ---------------------------------------------------------------------------
  private token(key: string): AppearanceToken | undefined {
    return this.tokens.find((t) => t.key === key);
  }

  private apply(key: string): void {
    if (!this.isBrowser) { return; }
    const t = this.token(key);
    if (!t) { return; }
    document.documentElement.style.setProperty(t.cssVar, this.overrides[key]);
    document.body.classList.add(t.bodyClass);
  }

  private unapply(key: string): void {
    if (!this.isBrowser) { return; }
    const t = this.token(key);
    if (!t) { return; }
    document.documentElement.style.removeProperty(t.cssVar);
    document.body.classList.remove(t.bodyClass);
  }

  private applyAll(): void {
    Object.keys(this.overrides).forEach((k) => this.apply(k));
  }

  private persist(): void {
    if (this.isBrowser) {
      try { localStorage.setItem(STORAGE_KEY, JSON.stringify(this.overrides)); } catch { /* quota */ }
    }
    this._changed.next();
  }
}
